import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ClearIcon from '@mui/icons-material/Clear';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import agent from '../../agent';

const LogCatchDialog = ({ open, onClose, freakPlaceId }) => {
  const [knownSpecies, setKnownSpecies] = useState([]);
  const [species, setSpecies] = useState('');
  const [caughtAt, setCaughtAt] = useState(dayjs());
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [exifCoords, setExifCoords] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    agent.FishSpecies.getAll().then(setKnownSpecies).catch(() => {});
  }, []);

  const handleClose = (success) => {
    setError('');
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setImagePreviewError(false);
    setExifCoords(null);
    onClose(success);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImagePreviewError(false);
    setExifCoords(null);
    try {
      const exifr = await import('exifr');
      const gps = await exifr.gps(file);
      if (gps?.latitude != null && gps?.longitude != null) {
        setExifCoords({ lat: gps.latitude, lon: gps.longitude });
      }
    } catch {
      // No GPS in EXIF or parsing failed — silent fail
    }
  };

  const handleRemoveImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    setImagePreviewError(false);
    setExifCoords(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!species.trim()) {
      setError('Podaj gatunek ryby');
      return;
    }
    setSubmitting(true);
    try {
      let imageUrl;
      if (imageFile) {
        const result = await agent.CatchLog.uploadImage(imageFile);
        imageUrl = result.imageUrl;
      }
      await agent.CatchLog.create({
        freakPlaceId,
        species: species.trim().toLowerCase(),
        caughtAt: caughtAt.toISOString(),
        imageUrl,
        ...(exifCoords ? { lat: exifCoords.lat, lon: exifCoords.lon } : {}),
      });
      setSpecies('');
      setCaughtAt(dayjs());
      handleClose(true);
    } catch (_e) {
      setError('Błąd zapisu. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => handleClose(false)} fullWidth maxWidth="xs">
      <DialogTitle>Zapisz połów</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            freeSolo
            options={[...knownSpecies, 'inny']}
            inputValue={species}
            onInputChange={(_, val) => setSpecies(val)}
            onChange={(_, val) => { if (val === 'inny') setSpecies(''); }}
            getOptionLabel={opt => opt === 'inny' ? 'inny (wpisz nazwę)' : opt}
            renderInput={(params) => (
              <TextField {...params} label="Gatunek" size="small" />
            )}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              label="Data i godzina połowu"
              value={caughtAt}
              onChange={val => val && setCaughtAt(val)}
              maxDateTime={dayjs()}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleImageChange}
          />

          {imageFile ? (
            <Box sx={{ position: 'relative' }}>
              {imagePreview && !imagePreviewError ? (
                <img
                  src={imagePreview}
                  alt="preview"
                  onError={() => setImagePreviewError(true)}
                  style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, display: 'block' }}
                />
              ) : (
                <Box sx={{ p: 1.5, border: `1px solid ${DEFAULT_THEME_COLOR}`, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddPhotoAlternateIcon sx={{ color: DEFAULT_THEME_COLOR }} />
                  <Typography variant="caption" noWrap sx={{ flex: 1 }}>{imageFile.name}</Typography>
                </Box>
              )}
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                sx={{
                  position: 'absolute', top: 4, right: 4,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={submitting ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                borderColor: DEFAULT_THEME_COLOR,
                color: DEFAULT_THEME_COLOR,
                '&:hover': { borderColor: DEFAULT_THEME_COLOR, bgcolor: 'rgba(54,201,162,0.06)' },
              }}
            >
              Dodaj zdjęcie (opcjonalne)
            </Button>
          )}

          {exifCoords && (
            <Chip
              icon={<GpsFixedIcon sx={{ fontSize: 14 }} />}
              label="Lokalizacja z zdjęcia"
              size="small"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: 'rgba(54,201,162,0.12)',
                color: DEFAULT_THEME_COLOR,
                border: `1px solid ${DEFAULT_THEME_COLOR}`,
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ bgcolor: DEFAULT_THEME_COLOR, '&:hover': { bgcolor: '#2aad8a' } }}
        >
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LogCatchDialog;
